// src/pages/builder/SuggestedTopicsPanel.js
//
// What to build next, and one click to put it everywhere it belongs.
//
// WHY IT LEADS WITH MULTI-STATE
// -----------------------------
// The last audit found 19,622 distinct gaps. Rendering that is not a list, it
// is a wall — nobody scrolls twenty thousand rows to decide what to record.
//
// 1,660 of them are needed by more than one state, and those are the ones
// where a single song pays for itself many times over: K-PS3-2 alone is
// roughly thirty state pages. So "Needed by many states" is the default view,
// served by one small document; the long tail loads a subject and grade at a
// time; and the rendered list is capped so a filter matching thousands cannot
// lock the browser.
//
// THEMES CROSS CODE SYSTEMS
// -------------------------
// Code grouping only unites states sharing a notation. Arkansas's 3.E.1 and
// Georgia's SS3E1 are the same idea and group as two rows of one state each.
// The theme view answers what codes cannot: "if I record a magnets song, how
// many states does that touch?" — 48 themes, covering 52% of gaps.
//
// Themes are advisory. Attaching happens per code, because two standards under
// one theme can be different grades or depths, and treating a theme as an
// identity would put a 5th grade song on a Kindergarten page.
//
// THE DATA IS A SNAPSHOT
// ----------------------
// contentGaps is written by `audit-standards-coverage.mjs --all --write-gaps`.
// Computing it live would be ~1,200 reads before anything could be drawn. The
// generated date is shown so a week-old number is never mistaken for today's.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  loadSummary,
  loadGradeGaps,
  saveSuggestedSlug,
  markComplete,
  unmarkGap,
} from "../../services/contentGapsService";
import "./layoutBuilderLibrary.css";

const SUBJECTS = [
  { key: "", label: "All subjects" },
  { key: "science", label: "Science" },
  { key: "math", label: "Math" },
  { key: "socialstudies", label: "Social Studies" },
];
const GRADES = ["k", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th"];

// Rendering is capped. A filter matching thousands of rows should show the
// most valuable ones and say so, not freeze the tab.
const MAX_ROWS = 120;

export default function SuggestedTopicsPanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [view, setView] = useState("multi");
  const [subject, setSubject] = useState("");
  const [grade, setGrade] = useState("");
  const [query, setQuery] = useState("");

  // Completed gaps are hidden by default. A backlog that keeps showing
  // finished work gets longer as you work through it, which is backwards.
  const [showDone, setShowDone] = useState(false);

  // "Everything" reads one subject+grade document at a time. The full backlog
  // is 19,000+ gaps; loading it whole to render 120 rows is what made this
  // panel sit on "Loading" for minutes.
  const [gradeGaps, setGradeGaps] = useState([]);
  const [gradeLoading, setGradeLoading] = useState(false);

  const [slugs, setSlugs] = useState({});
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState(null);
  const [confirming, setConfirming] = useState(null);
  // Which states the attach will actually touch. Defaults to all of them,
  // because "one song, every state that needs it" is the normal case — but a
  // topic that only suits one state's wording should not be forced onto
  // thirty, and there was no way to say so.
  const [picked, setPicked] = useState(null);

  // The confirm block renders below a table that can be 120 rows tall, so
  // pressing "Mark complete" on row 90 otherwise appears to do nothing — the
  // thing it opened is off-screen. Scroll to it, then back to the top of the
  // panel afterwards so the result banner is where the eye already is.
  const panelRef = useRef(null);
  const confirmRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const d = await loadSummary();
      setData(d);
      if (d) {
        const initial = {};
        for (const g of d.gaps) if (g.suggestedSlug) initial[g.code] = g.suggestedSlug;
        setSlugs((s) => ({ ...initial, ...s }));
      }
    } catch (err) {
      setError(err?.message || "Could not load the backlog.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && data === null && !loading) refresh();
  }, [open, data, loading, refresh]);

  useEffect(() => {
    if (confirming) setPicked(new Set(confirming.states.map((s2) => s2.state)));
  }, [confirming]);

  useEffect(() => {
    if (confirming && confirmRef.current) {
      confirmRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [confirming]);

  const backToTop = () =>
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  useEffect(() => {
    if (view !== "all" || !subject || !grade) {
      setGradeGaps([]);
      return undefined;
    }
    let alive = true;
    setGradeLoading(true);
    loadGradeGaps(subject, grade)
      .then((g) => { if (alive) setGradeGaps(g); })
      .catch((err) => { if (alive) setError(err?.message || "Could not load that grade."); })
      .finally(() => { if (alive) setGradeLoading(false); });
    return () => { alive = false; };
  }, [view, subject, grade]);

  const rows = useMemo(() => {
    if (!data) return [];
    const source = view === "all" ? gradeGaps : data.gaps;
    const q = query.trim().toLowerCase();

    let list = showDone ? source : source.filter((g) => !g.completedSlug);
    if (subject) list = list.filter((g) => g.subject === subject);
    if (grade) list = list.filter((g) => g.grade === grade);
    if (q) {
      list = list.filter(
        (g) =>
          g.code.toLowerCase().includes(q) ||
          String(g.description).toLowerCase().includes(q) ||
          String(g.theme || "").toLowerCase().includes(q)
      );
    }
    return [...list].sort(
      (a, b) => b.stateCount - a.stateCount || a.code.localeCompare(b.code)
    );
  }, [data, gradeGaps, view, subject, grade, query, showDone]);

  const themeRows = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return (data.themes || []).filter((t) => !q || t.theme.toLowerCase().includes(q));
  }, [data, query]);

  const onSlugChange = (code, value) => setSlugs((s) => ({ ...s, [code]: value }));

  const onSlugBlur = async (gap) => {
    const slug = (slugs[gap.code] || "").trim();
    if (slug === (gap.suggestedSlug || "")) return;
    try {
      await saveSuggestedSlug(gap.docId, gap.code, slug);
    } catch (err) {
      setError(`Could not save slug: ${err?.message || err}`);
    }
  };

  const doComplete = async (gap, only) => {
    const slug = (slugs[gap.code] || "").trim();
    // Narrow the gap to the chosen states before handing it over. The service
    // writes exactly what it is given, so the scope decision lives here.
    const scoped =
      only && only.size < gap.states.length
        ? { ...gap, states: gap.states.filter((s2) => only.has(s2.state)) }
        : gap;
    setConfirming(null);
    backToTop();
    setBusy(gap.code);
    setError("");
    setResult(null);
    try {
      const r = await markComplete(scoped, slug, (done, total, id) =>
        setProgress(`${done} of ${total} — ${id}`)
      );
      setResult({ code: gap.code, slug, ...r });
      await refresh();
    } catch (err) {
      setError(err?.message || "Attach failed.");
    } finally {
      setBusy("");
      setProgress("");
    }
  };

  const doUndo = async (gap) => {
    try {
      await unmarkGap(gap);
      await refresh();
    } catch (err) {
      setError(err?.message || "Could not undo.");
    }
  };

  return (
    <section className="lb-card lb-library" ref={panelRef}>
      <div className="lb-card-head">
        <span className="lb-step lb-step-alt">✦</span>
        <div>
          <h2>Suggested topics</h2>
          <p className="lb-hint">
            Standards with no topic yet, ordered by how many states need them.
            Record the topic, put the slug here, and attach it everywhere at once.
          </p>
        </div>
        <button
          type="button"
          className="lb-btn"
          onClick={() => setOpen((v) => !v)}
          style={{ marginLeft: "auto" }}
        >
          {open ? "Hide" : "Open"}
        </button>
      </div>

      {open && (
        <div className="lb-gaps-panel">
          {error && <p className="lb-hint lb-gaps-error">{error}</p>}
          {loading && <p className="lb-hint">Loading the backlog…</p>}

          {!loading && data === null && (
            <p className="lb-hint">
              No backlog yet. Generate it with:
              <br />
              <code>node src/scripts/audit-standards-coverage.mjs --all --write-gaps</code>
            </p>
          )}

          {!loading && data && (
            <>
              <p className="lb-hint">
                <strong>{Number(data.multiState || 0).toLocaleString()}</strong> gaps
                needed by more than one state, out of{" "}
                <strong>{Number(data.total || 0).toLocaleString()}</strong> in total
                {data.generatedAt && (
                  <> · as of {new Date(data.generatedAt).toLocaleDateString()}</>
                )}
                {" · "}
                <button type="button" className="lb-linkbtn" onClick={refresh}>
                  refresh
                </button>
              </p>

              <div className="lb-gaps-controls">
                <div className="lb-seg" role="group" aria-label="View">
                  {[
                    { k: "multi", l: "Needed by many states" },
                    { k: "theme", l: "By theme" },
                    { k: "all", l: "Everything" },
                  ].map((v) => (
                    <button
                      key={v.k}
                      type="button"
                      className={`lb-btn ${view === v.k ? "lb-btn-on" : ""}`}
                      onClick={() => setView(v.k)}
                    >
                      {v.l}
                    </button>
                  ))}
                </div>

                {view !== "theme" && (
                  <>
                    <select
                      className="lb-input"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      aria-label="Subject"
                    >
                      {SUBJECTS.map((s) => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                      ))}
                    </select>
                    <select
                      className="lb-input"
                      value={grade}
                      onChange={(e) => setGrade(e.target.value)}
                      aria-label="Grade"
                    >
                      <option value="">All grades</option>
                      {GRADES.map((g) => (
                        <option key={g} value={g}>
                          {g === "k" ? "Kindergarten" : `${g} grade`}
                        </option>
                      ))}
                    </select>
                    <label className="lb-gaps-check">
                      <input
                        type="checkbox"
                        checked={showDone}
                        onChange={(e) => setShowDone(e.target.checked)}
                      />
                      Show completed
                    </label>
                  </>
                )}

                <input
                  className="lb-input"
                  placeholder="Search code, text or theme…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Search"
                />
              </div>

              {result && (
                <p className="lb-hint lb-gaps-done">
                  <strong>{result.slug}</strong> attached for {result.code} —{" "}
                  {result.added} page(s) updated
                  {result.already ? `, ${result.already} already had it` : ""}
                  {result.missing ? `, ${result.missing} state(s) have no layout yet` : ""}.
                  Backups in <code>layoutBackups/*__{result.stamp}</code>. The Topic
                  Library state count is updated too — no script to re-run.
                </p>
              )}

              {view === "all" && (!subject || !grade) && (
                <p className="lb-hint">
                  Pick a subject and a grade. The full backlog is{" "}
                  {Number(data.total || 0).toLocaleString()} gaps — it loads one
                  subject and grade at a time rather than all at once.
                </p>
              )}
              {gradeLoading && <p className="lb-hint">Loading that grade…</p>}

              {view === "theme" ? (
                <table className="lb-gaps-table">
                  <thead>
                    <tr><th>Theme</th><th>States</th><th>Which</th></tr>
                  </thead>
                  <tbody>
                    {themeRows.map((t) => (
                      <tr key={t.theme}>
                        <td><strong>{t.theme}</strong></td>
                        <td><span className="lb-avail lb-avail-live">{t.stateCount}</span></td>
                        <td className="lb-gaps-states">{t.states.join(", ").toUpperCase()}</td>
                      </tr>
                    ))}
                    {!themeRows.length && (
                      <tr><td colSpan={3} className="lb-hint">No themes match that search.</td></tr>
                    )}
                  </tbody>
                </table>
              ) : (
                <>
                  {rows.length > MAX_ROWS && (
                    <p className="lb-hint">
                      Showing the {MAX_ROWS} highest-reach of{" "}
                      {rows.length.toLocaleString()} matches. Narrow it with the
                      filters or search to see the rest.
                    </p>
                  )}
                  <table className="lb-gaps-table">
                    <thead>
                      <tr>
                        <th>States</th>
                        <th>Standard</th>
                        <th>Theme</th>
                        <th>Topic slug</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(0, MAX_ROWS).map((g) => {
                        const slug = (slugs[g.code] || "").trim();
                        const running = busy === g.code;
                        const done = Boolean(g.completedSlug);
                        return (
                          <tr
                            key={`${g.docId}:${g.code}`}
                            className={done ? "lb-gaps-rowdone" : ""}
                          >
                            <td>
                              <span
                                className={`lb-avail ${
                                  g.stateCount > 1 ? "lb-avail-live" : "lb-avail-soon"
                                }`}
                              >
                                {g.stateCount}
                              </span>
                            </td>
                            <td>
                              <code className="lb-code">{g.code}</code>{" "}
                              <span className="lb-gaps-grade">
                                {g.grade} · {g.subject}
                              </span>
                              <div className="lb-gaps-desc">{g.description}</div>
                            </td>
                            <td className="lb-gaps-theme">{g.theme || "—"}</td>
                            <td>
                              <input
                                className="lb-input lb-gaps-slug"
                                placeholder="topic slug"
                                value={slugs[g.code] || ""}
                                onChange={(e) => onSlugChange(g.code, e.target.value)}
                                onBlur={() => onSlugBlur(g)}
                                aria-label={`Slug for ${g.code}`}
                              />
                            </td>
                            <td>
                              {done ? (
                                <button
                                  type="button"
                                  className="lb-btn"
                                  onClick={() => doUndo(g)}
                                  title={`Attached as ${g.completedSlug}. Click to put it back on the list.`}
                                >
                                  Done · undo
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="lb-btn"
                                  disabled={!slug || running}
                                  onClick={() => setConfirming(g)}
                                >
                                  {running ? "Attaching…" : "Mark complete"}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {!rows.length && (
                        <tr>
                          <td colSpan={5} className="lb-hint">
                            {view === "multi" && !data.hasMulti ? (
                              <>
                                No <code>contentGaps/_multi</code> document. That is
                                written by the current audit script — re-run it:
                                <br />
                                <code>
                                  node src/scripts/audit-standards-coverage.mjs --all --write-gaps
                                </code>
                              </>
                            ) : view === "multi" && !showDone ? (
                              <>
                                Nothing outstanding here. Tick <strong>Show completed</strong>{" "}
                                to see gaps already attached.
                              </>
                            ) : (
                              "Nothing matches those filters."
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </>
              )}

              {progress && <p className="lb-hint">Attaching… {progress}</p>}

              {/* One click here writes to every state page that needs this
                  standard — up to sixty documents. The list is shown before
                  anything is written, because the consequence is not obvious
                  from a button labelled "Mark complete". */}
              {confirming && (
                <div className="lb-gaps-confirm" ref={confirmRef}>
                  <p>
                    Attach <strong>{(slugs[confirming.code] || "").trim()}</strong> to{" "}
                    <strong>{confirming.stateCount}</strong> state
                    {confirming.stateCount === 1 ? "" : "s"} for{" "}
                    <code className="lb-code">{confirming.code}</code>,{" "}
                    {confirming.grade} {confirming.subject}?
                  </p>
                  <div className="lb-gaps-scope">
                    <label>
                      <input
                        type="radio"
                        checked={picked?.size === confirming.states.length}
                        onChange={() =>
                          setPicked(new Set(confirming.states.map((s2) => s2.state)))
                        }
                      />
                      All {confirming.states.length} state
                      {confirming.states.length === 1 ? "" : "s"} that teach this
                    </label>
                    <label>
                      <input
                        type="radio"
                        checked={picked?.size !== confirming.states.length}
                        onChange={() => setPicked(new Set())}
                      />
                      Only the states I pick
                    </label>
                  </div>

                  <div className="lb-gaps-picklist">
                    {confirming.states.map((s2) => (
                      <label key={s2.state} className="lb-gaps-pick">
                        <input
                          type="checkbox"
                          checked={picked?.has(s2.state) || false}
                          onChange={(e) =>
                            setPicked((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(s2.state);
                              else next.delete(s2.state);
                              return next;
                            })
                          }
                        />
                        {s2.state.toUpperCase()} <code className="lb-code">{s2.code}</code>
                      </label>
                    ))}
                  </div>

                  <p className="lb-hint">
                    Each state gets its own code, both student and educator views
                    are written, and every layout is backed up first.
                  </p>
                  <div className="lb-seg">
                    <button
                      type="button"
                      className="lb-btn lb-btn-on"
                      disabled={!picked || picked.size === 0}
                      onClick={() => doComplete(confirming, picked)}
                    >
                      Attach to {picked?.size || 0} state
                      {picked?.size === 1 ? "" : "s"}
                    </button>
                    <button
                      type="button"
                      className="lb-btn"
                      onClick={() => { setConfirming(null); backToTop(); }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
